<script>
    import { onMount, onDestroy, tick, untrack } from "svelte";
    import { params } from "@roxi/routify";
    import { format } from "svelte-i18n";
    import * as bootstrap from "bootstrap";

    import BreadCrumbNav from "../../../components/breadcrum-nav.svelte";
    import tagService from "../service.js";
    import vulnerabilityService from "../../vulnerability/service.js";
    import AssetList from "../../asset/_components/list.svelte";
    import FindingsList from "../../finding/_components/list.svelte";
    import VulnerabilityList from "../../vulnerability/_components/list.svelte";
    import Spinner from "../../../components/spinner.svelte";
    import powerTableService from "../../../services/powerTable.js";
    import VulnerabilityTitleRow from "../../../components/powerTable/vulnerability-title-row.svelte";
    import AddAssetsTag from "../_components/add-assets-tag.svelte";
    import AddFindingsTag from "../_components/add-findings-tag.svelte";
    import AddVulnerabilitiesTag from "../_components/add-vulnerabilities-tag.svelte";
    import RemoveTagAssets from "../_components/remove-tag-assets.svelte";
    import RemoveTagVulnerabilities from "../_components/remove-tag-vulnerabilities.svelte";
    import RemoveTagFindings from "../_components/remove-tag-findings.svelte";

    import { assets, cancelAssetSearch, searchAssets } from "../../asset/assets.state.svelte";
    import { findings, cancelFindingSearch, searchFindings } from "../../finding/findings.state.svelte";
    import { vulnerabilities, cancelVulnerabilitySearch, searchVulnerabilities } from "../../vulnerability/vulnerabilities.state.svelte";
    import { assetsLimits } from "../../user/user.state.svelte";

    // CRITICAL FIX: Hybrid ID resolution falls back to Routify URL params if prop is undefined
    let { id: propId = null } = $props();
    let id = $derived(propId || $params?.id);

    let addAssetsModal;
    let addFindingsModal;
    let addVulnerabilitiesModal;
    let removeAssetsModal;
    let removeFindingsModal;
    let removeVulnerabilitiesModal;

    let loadAddAssetsModal = $state(false);
    let loadAddFindingsModal = $state(false);
    let loadAddVulnerabilitiesModal = $state(false);

    let tags = $state([]);
    let tag = $state({});
    let associations = $state([]);
    let is_loading_tag = $state(true);
    let is_loading = $state({
        assets: false,
        findings: false,
        vulnerabilities: false,
    });

    // NUCLEAR OPTION: Intercepts all close triggers natively
    const handleModalDismiss = (e, modalInstance) => {
        if (e.target.closest('[data-bs-dismiss="modal"]')) {
            e.preventDefault();
            if (modalInstance) modalInstance.hide();
        }
    };

    const loadTagData = async (currentId) => {
        if (!currentId) return;
        is_loading_tag = true;
        try {
            let [fetchedTags, fetchedAssociations] = await Promise.all([
                tagService.search("v2/tag/", {}),
                tagService.search("v2/tag/association/", {})
            ]);

            tags = fetchedTags;
            associations = fetchedAssociations;
            let found = tags.find((t) => t.id === currentId);
            tag = found || {};
        } catch (err) {
            console.error("Error loading tag data:", err);
            tag = {};
        }
        is_loading_tag = false;
    };

    const getAssociatedData = async () => {
        if (!id) return;
        if (!findings.loading) {
            await getFindings({
                filter: { tag_id: id },
                buildList: {
                    tags: { elements: tags, associations: associations },
                },
            });
        }
        if (!vulnerabilities.loading) {
            await getVulnerabilities({
                filter: { tag_id: id },
                buildList: {
                    tags: { elements: tags, associations: associations },
                },
            });
        }
        if (!assets.loading) {
            await getAssets({
                filter: { tag_id: id, include_finding_count: true },
                buildList: {
                    tags: { elements: tags, associations: associations },
                },
            });
        }
    };

    const getAssets = async (config) => {
        is_loading.assets = true;
        let res = await searchAssets("v2/asset/", config);
        is_loading.assets = false;
        return res;
    };

    const getFindings = async (config) => {
        is_loading.findings = true;
        let res = await searchFindings("v2/finding/", config);
        is_loading.findings = false;
        return res;
    };

    const getVulnerabilities = async (config) => {
        is_loading.vulnerabilities = true;
        let res = await searchVulnerabilities("v2/vulnerability", config);
        is_loading.vulnerabilities = false;
        return res;
    };

    const handleAdd = async () => {
        if (addAssetsModal) addAssetsModal.hide();
        if (addFindingsModal) addFindingsModal.hide();
        if (addVulnerabilitiesModal) addVulnerabilitiesModal.hide();
        if (removeAssetsModal) removeAssetsModal.hide();
        if (removeFindingsModal) removeFindingsModal.hide();
        if (removeVulnerabilitiesModal) removeVulnerabilitiesModal.hide();

        await getAssociatedData();
    };

    let vulListOptions = {
        uniquePrefix: "vulnerabilityTable",
        rowsPerPageOptions: [10, 20, 50, 100],
        footerText: false,
        footerFilters: false,
        footerLoadingBar: false,
        segments: {
            topBar: ["search"],
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: powerTableService.getTableTranslations(),
        isDataRemote: false,
    };

    let vulListInstructs = [
        {
            key: "priority",
            title: $format("list.columns.priority"),
            filterable: false,
            parseAs: "unsafe-html",
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "title_es",
            title: $format("list.columns.title"),
            filterable: false,
            parseAs: "component",
            dataComponent: VulnerabilityTitleRow,
        },
        {
            key: "open",
            title: $format("list.columns.open"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "accepted",
            title: $format("list.columns.accepted"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "fixed",
            title: $format("list.columns.fixed"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "false_positive",
            title: $format("list.columns.false_positive"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "cvss",
            title: "CVSS",
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "epss",
            title: "EPSS",
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "tag",
            title: $format("list.columns.tag"),
            filterable: false,
            parseAs: "unsafe-html",
        },
    ];

    onMount(async () => {
        await tick();
        const bs = typeof bootstrap !== 'undefined' ? bootstrap : window.bootstrap;
        if (bs) {
            const addAstEl = document.getElementById("add-assets-modal-detail");
            if (addAstEl) addAssetsModal = new bs.Modal(addAstEl);

            const addFndEl = document.getElementById("add-findings-modal-detail");
            if (addFndEl) addFindingsModal = new bs.Modal(addFndEl);

            const addVulEl = document.getElementById("add-vuls-modal-detail");
            if (addVulEl) addVulnerabilitiesModal = new bs.Modal(addVulEl);

            const remAstEl = document.getElementById("remove-assets-modal-detail");
            if (remAstEl) removeAssetsModal = new bs.Modal(remAstEl);

            const remFndEl = document.getElementById("remove-findings-modal-detail");
            if (remFndEl) removeFindingsModal = new bs.Modal(remFndEl);

            const remVulEl = document.getElementById("remove-vuls-modal-detail");
            if (remVulEl) removeVulnerabilitiesModal = new bs.Modal(remVulEl);
        }
    });

    onDestroy(() => {
        cancelAssetSearch();
        cancelFindingSearch();
        cancelVulnerabilitySearch();
    });

    // Clean parallel data loading triggered whenever URL parameter ID changes
    $effect(() => {
        if (id) {
            untrack(() => {
                loadTagData(id).then(() => {
                    getAssociatedData();
                });
            });
        }
    });
</script>

<style>
    .breadcrumb {
        padding: 0 15px !important;
    }
</style>

{#if is_loading_tag}
    <section class="content-header">
        <div class="container-fluid">
            <div class="row">
                <div class="col-12 text-center p-5">
                    <Spinner />
                </div>
            </div>
        </div>
    </section>
{:else}
    <section class="content-header">
        <div class="container-fluid">
            <div class="row">
                <div class="col-12">
                    <h1 class="mt-3 mb-3 page-title">{tag.name || ""}</h1>
                </div>
                <div class="col-12 mt-3 breadcrumb">
                    <BreadCrumbNav
                        routes={[
                            { path: "/tag/", label: $format("tags") },
                            { label: tag.name || "" },
                        ]}
                    />
                </div>
            </div>
        </div>
    </section>

    <section class="content mb-5">
        <div class="container-fluid">
            <div class="row d-flex flex-row">
                <div class="col-12">
                    <div class="card">
                        <div class="card-body d-flex" style="gap: 15px;">
                            <button
                                class="btn btn-default mt-3"
                                id="addAssetsModalDetailBtn"
                                style="border-radius: 8px; height: 42px;"
                                onclick={() => { loadAddAssetsModal = true; if(addAssetsModal) addAssetsModal.show(); }}
                                disabled={is_loading.assets}
                            >
                                {$format("associate")} {$format("list.columns.assets")}
                            </button>
                            <button
                                class="btn btn-default mt-3"
                                id="addFindingsModalCustomBtn"
                                style="border-radius: 8px; height: 42px;"
                                disabled={is_loading.findings}
                                onclick={() => { loadAddFindingsModal = true; if(addFindingsModal) addFindingsModal.show(); }}
                            >
                                {$format("associate")} {$format("list.columns.findings")}
                            </button>
                            <button
                                class="btn btn-default mt-3"
                                id="AddVulModalDetailBtn"
                                style="border-radius: 8px; height: 42px;"
                                disabled={is_loading.vulnerabilities}
                                onclick={() => { loadAddVulnerabilitiesModal = true; if(addVulnerabilitiesModal) addVulnerabilitiesModal.show(); }}
                            >
                                {$format("associate")} {$format("list.columns.vulnerabilities")}
                            </button>
                            <button
                                class="btn btn-default mt-3"
                                id="removeAssetsModalDetailBtn"
                                style="border-radius: 8px; height: 42px;"
                                disabled={is_loading.assets}
                                onclick={() => removeAssetsModal && removeAssetsModal.show()}
                            >
                                {$format("remove")} {$format("list.columns.assets")}
                            </button>
                            <button
                                class="btn btn-default mt-3"
                                id="removeFindingsModalCustomBtn"
                                style="border-radius: 8px; height: 42px;"
                                disabled={is_loading.findings}
                                onclick={() => removeFindingsModal && removeFindingsModal.show()}
                            >
                                {$format("remove")} {$format("list.columns.findings")}
                            </button>
                            <button
                                class="btn btn-default mt-3"
                                id="RemoveVulModalDetailBtn"
                                style="border-radius: 8px; height: 42px;"
                                disabled={is_loading.vulnerabilities}
                                onclick={() => removeVulnerabilitiesModal && removeVulnerabilitiesModal.show()}
                            >
                                {$format("remove")} {$format("list.columns.vulnerabilities")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <section class="content">
        <div class="row">
            <div class="d-flex flex-column col-12" style="gap: 15px;">
                {#if is_loading.assets}
                    <div class="overlay-wrapper">
                        <div class="card">
                            <div class="card-header"><div class="card-title">{$format("asset.plural")}</div></div>
                            <div class="card-body d-flex flex-column justify-content-center" style="min-height: 500px;">
                                <div class="text-center overlay"><Spinner /></div>
                            </div>
                        </div>
                    </div>
                {:else if assets.list.data.length > 0}
                    <AssetList {tags} kind={"all"} showActions={false} />
                {/if}
            </div>

            <div class="d-flex flex-column col-12" style="gap: 15px;">
                {#if is_loading.findings}
                    <div class="overlay-wrapper">
                        <div class="card">
                            <div class="card-header"><div class="card-title">{$format("finding.plural")}</div></div>
                            <div class="card-body d-flex flex-column justify-content-center" style="min-height: 500px;">
                                <div class="text-center overlay"><Spinner /></div>
                            </div>
                        </div>
                    </div>
                {:else if findings.list.data.length > 0}
                    <FindingsList
                        {tags}
                        {associations}
                        parent={"finding"}
                        hasUserFilter={false}
                        showActions={false}
                    />
                {/if}
            </div>

            <div class="d-flex flex-column col-12" style="gap: 15px;">
                {#if is_loading.vulnerabilities}
                    <div class="overlay-wrapper">
                        <div class="card">
                            <div class="card-header"><div class="card-title">{$format("list.columns.vulnerabilities")}</div></div>
                            <div class="card-body d-flex flex-column justify-content-center" style="min-height: 500px;">
                                <div class="text-center overlay"><Spinner /></div>
                            </div>
                        </div>
                    </div>
                {:else if vulnerabilities.list.data.length > 0}
                    <VulnerabilityList
                        ptInstructs={vulListInstructs}
                        {tags}
                        showActions={false}
                        ptOptions={vulListOptions}
                    />
                {/if}
            </div>
        </div>
    </section>
{/if}

<!-- CRITICAL FIX: Modals moved to root component level so jQuery initializes cleanly without promise races -->
<div
    class="modal fade"
    id="add-assets-modal-detail"
    tabindex="-1"
    aria-hidden="true"
    onclickcapture={(e) => handleModalDismiss(e, addAssetsModal)}
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("associate")} {$format("list.columns.assets")}</h4>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
                {#if loadAddAssetsModal}
                    <AddAssetsTag
                        {tag}
                        limits={assetsLimits.data}
                        parent={"detail"}
                        on:save={handleAdd}
                        onsave={handleAdd}
                    />
                {/if}
            </div>
        </div>
    </div>
</div>

<div
    class="modal fade"
    id="add-findings-modal-detail"
    tabindex="-1"
    aria-hidden="true"
    onclickcapture={(e) => handleModalDismiss(e, addFindingsModal)}
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("associate")} {$format("list.columns.findings")}</h4>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
                {#if loadAddFindingsModal}
                    <AddFindingsTag
                        {tag}
                        assets={[]}
                        findings={[]}
                        vulnerabilities={[]}
                        limits={{}}
                        parent={"detail"}
                        on:save={handleAdd}
                        onsave={handleAdd}
                    />
                {/if}
            </div>
        </div>
    </div>
</div>

<div
    class="modal fade"
    id="add-vuls-modal-detail"
    tabindex="-1"
    aria-hidden="true"
    onclickcapture={(e) => handleModalDismiss(e, addVulnerabilitiesModal)}
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("associate")} {$format("list.columns.vulnerabilities")}</h4>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
                {#if loadAddVulnerabilitiesModal}
                    <AddVulnerabilitiesTag
                        {tag}
                        on:save={handleAdd}
                        onsave={handleAdd}
                        parent={"detail"}
                    />
                {/if}
            </div>
        </div>
    </div>
</div>

<div
    class="modal fade"
    id="remove-assets-modal-detail"
    tabindex="-1"
    aria-hidden="true"
    onclickcapture={(e) => handleModalDismiss(e, removeAssetsModal)}
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("buttons.delete")} {$format("list.columns.assets")}</h4>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
                <RemoveTagAssets
                    {tag}
                    assetsAssociated={$state.snapshot(assets.list.data)}
                    on:save={handleAdd}
                    onsave={handleAdd}
                />
            </div>
        </div>
    </div>
</div>

<div
    class="modal fade"
    id="remove-findings-modal-detail"
    tabindex="-1"
    aria-hidden="true"
    onclickcapture={(e) => handleModalDismiss(e, removeFindingsModal)}
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("buttons.delete")} {$format("list.columns.findings")}</h4>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
                <RemoveTagFindings
                    {tag}
                    vulnerabilities={$state.snapshot(vulnerabilities.list.data)}
                    assets={assets.list.data}
                    findingsAssociated={$state.snapshot(findings.list.data)}
                    parent={"detail"}
                    on:save={handleAdd}
                    onsave={handleAdd}
                />
            </div>
        </div>
    </div>
</div>

<div
    class="modal fade"
    id="remove-vuls-modal-detail"
    tabindex="-1"
    aria-hidden="true"
    onclickcapture={(e) => handleModalDismiss(e, removeVulnerabilitiesModal)}
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">Eliminar {$format("list.columns.vulnerabilities")}</h4>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
                <RemoveTagVulnerabilities
                    {tag}
                    vulnerabilitiesAssociated={$state.snapshot(vulnerabilities.list.data)}
                    parent={"detail"}
                    on:save={handleAdd}
                    onsave={handleAdd}
                />
            </div>
        </div>
    </div>
</div>